import React, { useEffect, useState } from "react";
import {
  getDemos,
  bookDemo,
  conductDemo,
  getOperators,
  getUsers,
  getLeads,
  cancelDemo,
  getDemoPersons,
} from "../../services/api";

import globalStyles from "../../styles/globalStyles";
import { getPageNumbers } from "../../helpers/Helpers";

import { FiSearch, FiEye } from "react-icons/fi";
import { MdOutlineMarkChatRead } from "react-icons/md";

import TvIcon from "@mui/icons-material/Tv";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { TbCalendarCancel } from "react-icons/tb";

const PRIMARY_COLOR = "#534AB7";

const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= breakpoint);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);

  return isMobile;
};

const s = {
  completeBtn: {
    border: "none",
    background: "#f8fafc",
    color: PRIMARY_COLOR,
    width: 32,
    height: 32,
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 15,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  cancelBtn: {
    border: "none",
    background: "#f8fafc",
    color: "#ef4444",
    width: 32,
    height: 32,
    borderRadius: 8,
    cursor: "pointer",
    fontSize: 15,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  srow: {
    display: "flex",
    gap: 10,
  },

  half: {
    flex: 1,
  },
};

const blankBook = {
  leadId: "",
  operatorId: "",
  scheduledAt: "",
  type: "",
  demoPersonName: "",
  meetingLink: "",
};

const blankConduct = {
  notes: "",
};

const blankCancel = {
  reason: "",
};

const DemoTile = ({ value, label, icon: Icon, color, active, onClick }) => (
  <div
    style={{ ...globalStyles.tile, ...(active ? globalStyles.activeTile : {}) }}
    onClick={onClick}
  >
    <div style={globalStyles.tileIcon(color)}>
      <Icon fontSize="medium" />
    </div>

    <div style={globalStyles.tileText}>
      <div style={globalStyles.tileValue}>{value ?? 0}</div>
      <div style={globalStyles.tileLabel}>{label}</div>
    </div>
  </div>
);

export default function Demo() {
  const [demos, setDemos] = useState([]);
  const [operators, setOperators] = useState([]);
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);

  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [bookModal, setBookModal] = useState(false);
  const [conductModal, setConductModal] = useState(false);
  const [cancelModal, setCancelModal] = useState(false);

  const [view, setView] = useState(null);

  const [selectedDemo, setSelectedDemo] = useState(null);
  const [bookForm, setBookForm] = useState(blankBook);
  const [conductForm, setConductForm] = useState(blankConduct);
  const [cancelForm, setCancelForm] = useState(blankCancel);

  const [err, setErr] = useState("");
  const [fieldErr, setFieldErr] = useState({});

  const [page, setPage] = useState(1);
  const [limit] = useState(20);

  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLastPage, setIsLastPage] = useState(false);
  const [demoPersons, setDemoPersons] = useState([]);
  const isMobile = useIsMobile();

  const [demoCounts, setDemoCounts] = useState({
    total: 0,
    scheduled: 0,
    completed: 0,
    cancelled: 0,
  });

  const getDemoStatus = (demo) =>
    demo.demoStatus || demo.status || "not_scheduled";

  const getBusinessTypeValues = (value) => {
    if (Array.isArray(value)) {
      return value
        .flatMap((item) => String(item || "").split(","))
        .map((item) => item.trim())
        .filter(Boolean);
    }

    return String(value || "")
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const getBusinessTypeText = (value) => {
    const types = getBusinessTypeValues(value);

    if (!types.length) return "—";

    return types
      .map((type) =>
        type.toLowerCase() === "cctv"
          ? "CCTV"
          : type
              .split(" ")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" "),
      )
      .join(", ");
  };

  const normalizeText = (value) => {
    return getBusinessTypeValues(value).join(" ").toLowerCase();
  };

  const formatDemoType = (value) => {
    if (!value) return "—";
    return String(value).charAt(0).toUpperCase() + String(value).slice(1);
  };

  const formatDemoStatus = (value) => {
    if (!value) return "—";
    return String(value)
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" \ ");
  };

  const formatDateInputValue = (date) => {
    if (!date) return "";
    return new Date(date).toISOString().slice(0, 10);
  };

  const formatDate = (date) => {
    if (!date) return "—";

    return new Date(date).toLocaleString("en-IN", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const load = () => {
    setLoading(true);

    const params = {
      page,
      limit,
    };

    if (search) params.search = search;
    if (typeFilter) params.type = typeFilter;
    if (statusFilter) params.status = statusFilter;
    if (fromDate) {
      params.fromDate = new Date(fromDate).toISOString();
    }
    if (toDate) {
      const endDate = new Date(toDate);
      endDate.setHours(23, 59, 59, 999);

      params.toDate = endDate.toISOString();
    }

    Promise.all([
      getDemos(params),
      getDemos({ limit: 1000 }),
      getUsers({ limit: 500 }),
      getOperators({ limit: 500 }),
      getLeads({ limit: 500 }),
      getDemoPersons({ limit: 500 }),
    ])
      .then(
        ([
          demoResponse,
          allDemoResponse,
          userResponse,
          operatorResponse,
          leadResponse,
          demoPersonResponse,
        ]) => {
          const allDemoData = allDemoResponse?.data?.data || {};
          let apiDemos = Array.isArray(allDemoData.demos)
            ? allDemoData.demos
            : [];

          const normalizedSearch = search.trim().toLowerCase();

          if (normalizedSearch) {
            apiDemos = apiDemos.filter((demo) => {
              const operatorName = demo.operatorId?.name || "";
              const operatorPhone = demo.operatorId?.phone || "";
              const businessType = demo.operatorId?.businessType || "";
              const demoPersonName = demo.demoPersonName || "";
              const conductedByName = demo.conductedBy?.name || "";
              const status = getDemoStatus(demo);
              const scheduledAt = formatDate(demo.scheduledAt);

              return (
                operatorName.toLowerCase().includes(normalizedSearch) ||
                operatorPhone.toLowerCase().includes(normalizedSearch) ||
                normalizeText(businessType).includes(normalizedSearch) ||
                demoPersonName.toLowerCase().includes(normalizedSearch) ||
                conductedByName.toLowerCase().includes(normalizedSearch) ||
                String(demo.type || "")
                  .toLowerCase()
                  .includes(normalizedSearch) ||
                String(status || "")
                  .toLowerCase()
                  .includes(normalizedSearch) ||
                scheduledAt.toLowerCase().includes(normalizedSearch)
              );
            });
          }

          if (typeFilter) {
            apiDemos = apiDemos.filter(
              (demo) =>
                String(demo.type || "").toLowerCase() ===
                typeFilter.toLowerCase(),
            );
          }

          if (statusFilter) {
            apiDemos = apiDemos.filter(
              (demo) =>
                String(getDemoStatus(demo) || "").toLowerCase() ===
                statusFilter.toLowerCase(),
            );
          }

          if (fromDate) {
            apiDemos = apiDemos.filter((demo) => {
              return formatDateInputValue(demo.scheduledAt) >= fromDate;
            });
          }

          if (toDate) {
            apiDemos = apiDemos.filter((demo) => {
              return formatDateInputValue(demo.scheduledAt) <= toDate;
            });
          }

          setDemos(apiDemos);
          setTotal(apiDemos.length);
          setTotalPages(1);
          setIsLastPage(true);

          const allDemos = allDemoResponse?.data?.data?.demos || [];

          setDemoCounts({
            total: allDemoResponse?.data?.data?.total || allDemos.length,
            scheduled: allDemos.filter(
              (demo) => getDemoStatus(demo) === "scheduled",
            ).length,
            completed: allDemos.filter(
              (demo) => getDemoStatus(demo) === "completed",
            ).length,
            cancelled: allDemos.filter(
              (demo) => getDemoStatus(demo) === "cancelled",
            ).length,
          });

          const apiUsers =
            userResponse?.data?.data?.users || userResponse?.data?.data || [];

          const apiOperators = operatorResponse?.data?.data?.operators || [];

          const apiDemoPersons = demoPersonResponse?.data?.data || [];

          const apiLeads =
            leadResponse?.data?.data?.leads ||
            leadResponse?.data?.leads ||
            leadResponse?.data?.data ||
            [];

          setUsers(Array.isArray(apiUsers) ? apiUsers : []);
          setOperators(Array.isArray(apiOperators) ? apiOperators : []);
          setLeads(Array.isArray(apiLeads) ? apiLeads : []);
          setDemoPersons(Array.isArray(apiDemoPersons) ? apiDemoPersons : []);
        },
      )
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [page, search, typeFilter, statusFilter, fromDate, toDate]);

  const getFieldError = (key) => {
    const error = fieldErr[key];

    if (Array.isArray(error)) return error[0];

    if (typeof error === "object" && error?.message) {
      return error.message;
    }

    return error || "";
  };

  const getLeadOperatorId = (lead) => {
    if (!lead?.operatorId) return "";
    return lead.operatorId._id || lead.operatorId.id || lead.operatorId || "";
  };

  const getLeadLabel = (lead) => {
    const operatorName =
      lead.operatorId?.name || lead.operatorName || "No Operator";
    const operatorPhone =
      lead.operatorId?.phone ||
      lead.operatorId?.mobile ||
      lead.phone ||
      lead.mobile ||
      "—";

    return `${operatorName} (${operatorPhone})`;
  };

  const demoScheduledLeads = leads.filter(
    (lead) => lead.currentStage === "demo_scheduled",
  );

  const handleTileClick = (status) => {
    setStatusFilter(status);
    setPage(1);
  };

  const openBookDemo = () => {
    setBookForm({ ...blankBook });
    setErr("");
    setFieldErr({});
    setBookModal(true);
  };

  const openConductDemo = (demo) => {
    setSelectedDemo(demo);

    setConductForm({
      notes: demo.notes || demo.remarks || "",
    });

    setErr("");
    setFieldErr({});
    setConductModal(true);
  };

  const openCancelDemo = (demo) => {
    setSelectedDemo(demo);

    setCancelForm({
      reason: demo.reason || "",
    });

    setErr("");
    setFieldErr({});
    setCancelModal(true);
  };

  const openView = (demo) => {
    setView(demo);
  };

  const setBook = (key, value) => {
    setBookForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const setConduct = (key, value) => {
    setConductForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const setCancel = (key, value) => {
    setCancelForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleBookSubmit = async (e) => {
    e.preventDefault();

    setErr("");
    setFieldErr({});

    const errors = {};

    if (!bookForm.leadId) errors.leadId = "Lead is required";
    if (!bookForm.operatorId) errors.operatorId = "Operator is required";
    if (!bookForm.type) errors.type = "Demo type is required";
    if (!bookForm.scheduledAt)
      errors.scheduledAt = "Scheduled date and time is required";
    if (!bookForm.demoPersonName.trim()) {
      errors.demoPersonName = "Demo person name is required";
    }
    if (bookForm.type === "online" && !bookForm.meetingLink.trim()) {
      errors.meetingLink = "Meeting link is required";
    }

    if (Object.keys(errors).length > 0) {
      setFieldErr(errors);
      return;
    }

    const payload = {
      leadId: bookForm.leadId,
      operatorId: bookForm.operatorId,
      scheduledAt: new Date(bookForm.scheduledAt).toISOString(),
      type: bookForm.type,
      demoPersonName: bookForm.demoPersonName.trim(),
      ...(bookForm.type === "online"
        ? { meetingLink: bookForm.meetingLink.trim() }
        : {}),
    };

    try {
      await bookDemo(payload);

      setBookModal(false);
      setBookForm({ ...blankBook });
      load();
    } catch (error) {
      console.log("Book demo error:", error.response?.data);

      const apiErrors = error.response?.data?.errors || {};
      const message = error.response?.data?.message || "Book demo failed";

      if (Object.keys(apiErrors).length > 0) {
        setFieldErr(apiErrors);
        setErr("");
      } else {
        setFieldErr({});
        setErr(message);
      }
    }
  };

  const handleConductSubmit = async (e) => {
    e.preventDefault();

    if (!selectedDemo?._id) return;

    setErr("");
    setFieldErr({});

    const payload = {
      notes: conductForm.notes,
    };

    try {
      await conductDemo(selectedDemo._id, payload);

      setConductModal(false);
      setSelectedDemo(null);
      setConductForm({ ...blankConduct });
      load();
    } catch (error) {
      console.log("Mark status completed error:", error.response?.data);

      const apiErrors = error.response?.data?.errors || {};
      const message =
        error.response?.data?.message || "Mark status completed failed";

      if (Object.keys(apiErrors).length > 0) {
        setFieldErr(apiErrors);
        setErr("");
      } else {
        setFieldErr({});
        setErr(message);
      }
    }
  };

  const handleCancelDemo = async (e) => {
    e.preventDefault();

    if (!selectedDemo?._id) return;

    setErr("");
    setFieldErr({});

    const payload = {
      reason: cancelForm.reason,
    };

    try {
      await cancelDemo(selectedDemo._id, payload);

      setCancelModal(false);
      setSelectedDemo(null);
      setCancelForm({ ...blankCancel });
      load();
    } catch (error) {
      console.log("Mark Demo Cancelled error:", error.response?.data);

      const apiErrors = error.response?.data?.errors || {};
      const message =
        error.response?.data?.message || "Mark Demo Cancelled failed";

      if (Object.keys(apiErrors).length > 0) {
        setFieldErr(apiErrors);
        setErr("");
      } else {
        setFieldErr({});
        setErr(message);
      }
    }
  };

  return (
    <div style={globalStyles.contentWrapper}>
      <div style={globalStyles.row}>
        <h1 style={globalStyles.h1}>Demos</h1>

        <button
          type="button"
          style={{
            ...globalStyles.btn,
            ...(isMobile ? { width: "100%", justifyContent: "center" } : {}),
          }}
          onClick={openBookDemo}
        >
          + Book Demo
        </button>
      </div>

      <div
        style={{
          ...globalStyles.tilesGrid,
          ...(isMobile ? globalStyles.responsive.mobileTilesGrid : {}),
        }}
      >
        <DemoTile
          value={demoCounts.total}
          label="Total Demos"
          icon={TvIcon}
          color="#06b6d4"
          active={statusFilter === ""}
          onClick={() => handleTileClick("")}
        />

        <DemoTile
          value={demoCounts.scheduled}
          label="Scheduled"
          icon={EventAvailableIcon}
          color="#2563eb"
          active={statusFilter === "scheduled"}
          onClick={() => handleTileClick("scheduled")}
        />

        <DemoTile
          value={demoCounts.completed}
          label="Completed"
          icon={CheckCircleIcon}
          color="#10b981"
          active={statusFilter === "completed"}
          onClick={() => handleTileClick("completed")}
        />

        <DemoTile
          value={demoCounts.cancelled}
          label="Cancelled"
          icon={CancelIcon}
          color="#ef4444"
          active={statusFilter === "cancelled"}
          onClick={() => handleTileClick("cancelled")}
        />
      </div>

      <div
        style={{
          ...globalStyles.toolbar,
          ...(isMobile ? globalStyles.responsive.mobileToolbar : {}),
        }}
      >
        <div
          style={{
            ...globalStyles.filterLeft,
            ...(isMobile ? globalStyles.responsive.mobileFilterLeft : {}),
          }}
        >
          <div
            style={{
              ...globalStyles.searchWrap,
              ...(isMobile ? globalStyles.responsive.mobileSearchWrap : {}),
            }}
          >
            <FiSearch style={globalStyles.searchIcon} />

            <input
              style={globalStyles.searchInput}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search demos..."
            />
          </div>

          <select
            style={{
              ...globalStyles.filterSelect,
              ...(isMobile ? globalStyles.responsive.mobileFilterSelect : {}),
            }}
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Select Demo Type</option>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
          </select>

          <select
            style={{
              ...globalStyles.filterSelect,
              ...(isMobile ? globalStyles.responsive.mobileFilterSelect : {}),
            }}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Select Demo Status</option>
            <option value="scheduled">Scheduled</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <div
            style={{
              display: "flex",
              alignItems: isMobile ? "stretch" : "center",
              gap: 8,
              ...(isMobile ? { width: "100%", flexDirection: "column" } : {}),
            }}
          >
            <label style={{ fontSize: 13, color: "#374151" }}>From</label>

            <input
              type="date"
              style={{
                ...globalStyles.filterSelect,
                ...(isMobile ? globalStyles.responsive.mobileFilterSelect : {}),
              }}
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setPage(1);
              }}
            />
          </div>

          <div
            style={{
              display: "flex",
              alignItems: isMobile ? "stretch" : "center",
              gap: 8,
              ...(isMobile ? { width: "100%", flexDirection: "column" } : {}),
            }}
          >
            <label style={{ fontSize: 13, color: "#374151" }}>To</label>

            <input
              type="date"
              style={{
                ...globalStyles.filterSelect,
                ...(isMobile ? globalStyles.responsive.mobileFilterSelect : {}),
              }}
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setPage(1);
              }}
            />
          </div>

          {/* {(search || typeFilter || statusFilter || fromDate || toDate) && (
            // <button
            //   type="button"
            //   style={{
            //     ...globalStyles.filterSelect,
            //     color: PRIMARY_COLOR,
            //     fontWeight: 700,
            //     background: "#f1f0ff",
            //     cursor: "pointer",
            //     ...(isMobile ? globalStyles.responsive.mobileFilterSelect : {}),
            //   }}
            //   onClick={() => {
            //     setSearch("");
            //     setTypeFilter("");
            //     setStatusFilter("");
            //     setFromDate("");
            //     setToDate("");
            //     setPage(1);
            //   }}
            // >
            //   Clear Filters
            // </button>
          )} */}
        </div>
        <span style={globalStyles.paginatePageInfo}>
          Page {page} of {totalPages}
        </span>
      </div>

      {loading ? (
        <div style={globalStyles.loading}>Loading demos...</div>
      ) : (
        <div style={globalStyles.tableScroll}>
          <table style={globalStyles.table}>
            <thead>
              <tr>
                <th style={globalStyles.th}>Operator</th>
                <th style={globalStyles.th}>Business Type</th>
                <th style={globalStyles.th}>Type</th>
                <th style={globalStyles.th}>Scheduled At</th>
                <th style={globalStyles.th}>Status</th>
                <th style={globalStyles.th}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {demos.length > 0 ? (
                demos.map((demo) => {
                  const status = getDemoStatus(demo);

                  return (
                    <tr key={demo._id}>
                      <td style={globalStyles.td}>
                        <strong>{demo.operatorId?.name || "—"}</strong>
                        <br />
                        <span style={{ fontSize: 12, color: "#94a3b8" }}>
                          {demo.operatorId?.phone || "—"}
                        </span>
                      </td>

                      <td style={globalStyles.td}>
                        {getBusinessTypeText(demo.operatorId?.businessType)}
                      </td>

                      <td style={globalStyles.td}>
                        {formatDemoType(demo.type)}
                      </td>

                      <td style={globalStyles.td}>
                        {formatDate(demo.scheduledAt)}
                      </td>

                      <td style={globalStyles.td}>
                        <span style={globalStyles.statusBadge(status)}>
                          {formatDemoStatus(status)}
                        </span>
                      </td>

                      <td style={globalStyles.td}>
                        <div style={globalStyles.btnRow}>
                          <button
                            type="button"
                            title="View"
                            style={globalStyles.viewBtn}
                            onClick={() => openView(demo)}
                          >
                            <FiEye size={14} />
                          </button>

                          {status === "scheduled" && (
                            <button
                              type="button"
                              title="Mark Status Completed"
                              style={s.completeBtn}
                              onClick={() => openConductDemo(demo)}
                            >
                              <MdOutlineMarkChatRead size={15} />
                            </button>
                          )}

                          {status === "scheduled" && (
                            <button
                              type="button"
                              title="Mark Demo Cancelled"
                              style={s.cancelBtn}
                              onClick={() => openCancelDemo(demo)}
                            >
                              <TbCalendarCancel size={15} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan="6"
                    style={{ ...globalStyles.td, ...globalStyles.emptyState }}
                  >
                    No Demos Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && totalPages > 1 && (
        <div style={globalStyles.paginateRow}>
          <button
            type="button"
            style={{
              ...globalStyles.paginateBtn,
              opacity: page === 1 ? 0.5 : 1,
            }}
            disabled={page === 1}
            onClick={() => setPage(1)}
          >
            {"<<"}
          </button>

          <button
            type="button"
            style={{
              ...globalStyles.paginateBtn,
              opacity: page === 1 ? 0.5 : 1,
            }}
            disabled={page === 1}
            onClick={() => setPage((prev) => prev - 1)}
          >
            {"<"}
          </button>

          {getPageNumbers(page, totalPages).map((pageNumber) => (
            <button
              key={pageNumber}
              type="button"
              style={{
                ...globalStyles.paginateBtn,
                background: page === pageNumber ? PRIMARY_COLOR : "#f8fafc",
                color: page === pageNumber ? "#fff" : PRIMARY_COLOR,
                minWidth: 36,
              }}
              onClick={() => setPage(pageNumber)}
            >
              {pageNumber}
            </button>
          ))}

          <button
            type="button"
            style={{
              ...globalStyles.paginateBtn,
              opacity: isLastPage ? 0.5 : 1,
            }}
            disabled={isLastPage}
            onClick={() => setPage((prev) => prev + 1)}
          >
            {">"}
          </button>

          <button
            type="button"
            style={{
              ...globalStyles.paginateBtn,
              opacity: isLastPage ? 0.5 : 1,
            }}
            disabled={isLastPage}
            onClick={() => setPage(totalPages)}
          >
            {">>"}
          </button>
        </div>
      )}

      {bookModal && (
        <div style={globalStyles.modal}>
          <div
            style={{
              ...globalStyles.mcard,
              ...(isMobile ? globalStyles.responsive.mobileModalCard : {}),
            }}
          >
            <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20 }}>
              Book Demo
            </h2>

            {err && (
              <div style={{ color: "#ef4444", marginBottom: 12, fontSize: 13 }}>
                {err}
              </div>
            )}

            <form onSubmit={handleBookSubmit}>
              <div
                style={{
                  ...s.srow,
                  ...(isMobile ? globalStyles.responsive.mobileFormRow : {}),
                }}
              >
                <div style={s.half}>
                  <label style={globalStyles.label}>Lead *</label>

                  <select
                    style={{
                      ...globalStyles.select,
                      ...(getFieldError("leadId") || getFieldError("operatorId")
                        ? globalStyles.inputError
                        : {}),
                    }}
                    value={bookForm.leadId}
                    onChange={(e) => {
                      const leadId = e.target.value;
                      const selectedLead = leads.find(
                        (lead) => lead._id === leadId || lead.id === leadId,
                      );

                      setBookForm((prev) => ({
                        ...prev,
                        leadId,
                        operatorId: getLeadOperatorId(selectedLead),
                      }));
                    }}
                  >
                    <option value="">Select Lead</option>

                    {demoScheduledLeads.map((lead) => (
                      <option
                        key={lead._id || lead.id}
                        value={lead._id || lead.id}
                      >
                        {getLeadLabel(lead)}
                      </option>
                    ))}
                  </select>

                  {(getFieldError("leadId") || getFieldError("operatorId")) && (
                    <div style={globalStyles.errorText}>
                      {getFieldError("leadId") || getFieldError("operatorId")}
                    </div>
                  )}
                </div>

                <div style={s.half}>
                  <label style={globalStyles.label}>Demo Type *</label>

                  <select
                    style={{
                      ...globalStyles.select,
                      ...(getFieldError("type") ? globalStyles.inputError : {}),
                    }}
                    value={bookForm.type}
                    onChange={(e) => {
                      const value = e.target.value;

                      setBookForm((prev) => ({
                        ...prev,
                        type: value,
                        meetingLink: value === "online" ? prev.meetingLink : "",
                      }));
                    }}
                  >
                    <option value="">Select Demo Type</option>
                    <option value="online">Online</option>
                    <option value="offline">Offline</option>
                  </select>

                  {getFieldError("type") && (
                    <div style={globalStyles.errorText}>
                      {getFieldError("type")}
                    </div>
                  )}
                </div>
              </div>

              <div
                style={{
                  ...s.srow,
                  ...(isMobile ? globalStyles.responsive.mobileFormRow : {}),
                }}
              >
                <div style={s.half}>
                  <label style={globalStyles.label}>Scheduled At *</label>

                  <input
                    style={{
                      ...globalStyles.input,
                      ...(getFieldError("scheduledAt")
                        ? globalStyles.inputError
                        : {}),
                    }}
                    type="datetime-local"
                    value={bookForm.scheduledAt}
                    onChange={(e) => setBook("scheduledAt", e.target.value)}
                  />

                  {getFieldError("scheduledAt") && (
                    <div style={globalStyles.errorText}>
                      {getFieldError("scheduledAt")}
                    </div>
                  )}
                </div>

                <div style={s.half}>
                  <label style={globalStyles.label}>Demo Person Name *</label>
                  <select
                    style={{
                      ...globalStyles.select,
                      ...(getFieldError("demoPersonName")
                        ? globalStyles.inputError
                        : {}),
                    }}
                    value={bookForm.demoPersonName}
                    onChange={(e) => setBook("demoPersonName", e.target.value)}
                  >
                    <option value="">Select Demo Person</option>

                    {demoPersons.map((person) => (
                      <option key={person.id} value={person.name}>
                        {person.name} ({person.phone})
                      </option>
                    ))}
                  </select>

                  {getFieldError("demoPersonName") && (
                    <div style={globalStyles.errorText}>
                      {getFieldError("demoPersonName")}
                    </div>
                  )}
                </div>
              </div>

              {bookForm.type === "online" && (
                <div
                  style={{
                    ...s.srow,
                    ...(isMobile ? globalStyles.responsive.mobileFormRow : {}),
                  }}
                >
                  <div style={s.half}>
                    <label style={globalStyles.label}>Meeting Link *</label>

                    <input
                      style={{
                        ...globalStyles.input,
                        ...(getFieldError("meetingLink")
                          ? globalStyles.inputError
                          : {}),
                      }}
                      value={bookForm.meetingLink}
                      onChange={(e) => setBook("meetingLink", e.target.value)}
                      placeholder="Enter online meeting link"
                    />

                    {getFieldError("meetingLink") && (
                      <div style={globalStyles.errorText}>
                        {getFieldError("meetingLink")}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div
                style={{
                  ...globalStyles.mrow,
                  ...(isMobile ? { justifyContent: "stretch" } : {}),
                }}
              >
                <button
                  type="button"
                  style={{
                    ...globalStyles.cancel,
                    ...(isMobile ? { width: "100%" } : {}),
                  }}
                  onClick={() => setBookModal(false)}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  style={{
                    ...globalStyles.btn,
                    ...(isMobile
                      ? { width: "100%", justifyContent: "center" }
                      : {}),
                  }}
                >
                  Book Demo
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {conductModal && (
        <div style={globalStyles.modal}>
          <div
            style={{
              ...globalStyles.mcard,
              ...(isMobile ? globalStyles.responsive.mobileModalCard : {}),
            }}
          >
            <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20 }}>
              Mark Status Completed
            </h2>

            {err && (
              <div style={{ color: "#ef4444", marginBottom: 12, fontSize: 13 }}>
                {err}
              </div>
            )}

            <form onSubmit={handleConductSubmit}>
              <label style={globalStyles.label}>Notes</label>
              <textarea
                style={{
                  ...globalStyles.textarea,
                  ...(getFieldError("notes") ? globalStyles.inputError : {}),
                }}
                value={conductForm.notes}
                onChange={(e) => setConduct("notes", e.target.value)}
                placeholder="Enter Status notes"
              />
              {getFieldError("notes") && (
                <div style={globalStyles.errorText}>
                  {getFieldError("notes")}
                </div>
              )}
              <div
                style={{
                  ...globalStyles.mrow,
                  ...(isMobile ? { justifyContent: "stretch" } : {}),
                }}
              >
                <button
                  type="button"
                  style={{
                    ...globalStyles.cancel,
                    ...(isMobile ? { width: "100%" } : {}),
                  }}
                  onClick={() => setConductModal(false)}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  style={{
                    ...globalStyles.btn,
                    ...(isMobile
                      ? { width: "100%", justifyContent: "center" }
                      : {}),
                  }}
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {cancelModal && (
        <div style={globalStyles.modal}>
          <div
            style={{
              ...globalStyles.mcard,
              ...(isMobile ? globalStyles.responsive.mobileModalCard : {}),
            }}
          >
            <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20 }}>
              Mark Demo Cancelled
            </h2>

            {err && (
              <div style={{ color: "#ef4444", marginBottom: 12, fontSize: 13 }}>
                {err}
              </div>
            )}

            <form onSubmit={handleCancelDemo}>
              <label style={globalStyles.label}>Reason</label>
              <textarea
                style={{
                  ...globalStyles.textarea,
                  ...(getFieldError("reason") ? globalStyles.inputError : {}),
                }}
                value={cancelForm.reason}
                onChange={(e) => setCancel("reason", e.target.value)}
                placeholder="Enter cancellation reason"
              />

              {getFieldError("reason") && (
                <div style={globalStyles.errorText}>
                  {getFieldError("reason")}
                </div>
              )}

              <div
                style={{
                  ...globalStyles.mrow,
                  ...(isMobile ? { justifyContent: "stretch" } : {}),
                }}
              >
                <button
                  type="button"
                  style={{
                    ...globalStyles.cancel,
                    ...(isMobile ? { width: "100%" } : {}),
                  }}
                  onClick={() => setCancelModal(false)}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  style={{
                    ...globalStyles.btn,
                    ...(isMobile
                      ? { width: "100%", justifyContent: "center" }
                      : {}),
                  }}
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {view && (
        <div style={globalStyles.modal}>
          <div
            style={{
              ...globalStyles.mcard,
              ...(isMobile ? globalStyles.responsive.mobileModalCard : {}),
            }}
          >
            <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 20 }}>
              View Demo
            </h2>

            <div
              style={{
                ...globalStyles.viewGrid,
                ...(isMobile ? { gridTemplateColumns: "1fr" } : {}),
              }}
            >
              <div style={globalStyles.viewItem}>
                <div style={globalStyles.viewLabel}>Operator Name</div>
                <div style={globalStyles.viewValue}>
                  {view.operatorId?.name || "—"}
                </div>
              </div>

              <div style={globalStyles.viewItem}>
                <div style={globalStyles.viewLabel}>Operator Phone</div>
                <div style={globalStyles.viewValue}>
                  {view.operatorId?.phone || "—"}
                </div>
              </div>

              <div style={globalStyles.viewItem}>
                <div style={globalStyles.viewLabel}>Business Type</div>
                <div style={globalStyles.viewValue}>
                  {getBusinessTypeText(view.operatorId?.businessType)}
                </div>
              </div>

              <div style={globalStyles.viewItem}>
                <div style={globalStyles.viewLabel}>Demo Type</div>
                <div style={globalStyles.viewValue}>{view.type || "—"}</div>
              </div>

              <div style={globalStyles.viewItem}>
                <div style={globalStyles.viewLabel}>Status</div>
                <div style={globalStyles.viewValue}>
                  <span style={globalStyles.statusBadge(getDemoStatus(view))}>
                    {formatDemoStatus(getDemoStatus(view))}
                  </span>
                </div>
              </div>

              <div style={globalStyles.viewItem}>
                <div style={globalStyles.viewLabel}>Scheduled At</div>
                <div style={globalStyles.viewValue}>
                  {formatDate(view.scheduledAt)}
                </div>
              </div>

              <div style={globalStyles.viewItem}>
                <div style={globalStyles.viewLabel}>Demo Person Name</div>
                <div style={globalStyles.viewValue}>
                  {view.demoPersonName || "—"}
                </div>
              </div>

              <div style={globalStyles.viewItem}>
                <div style={globalStyles.viewLabel}>Meeting Link</div>
                <div style={globalStyles.viewValue}>
                  {view.meetingLink ? (
                    <a
                      href={view.meetingLink}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: PRIMARY_COLOR, textDecoration: "none" }}
                    >
                      {view.meetingLink}
                    </a>
                  ) : (
                    "—"
                  )}
                </div>
              </div>

              <div style={globalStyles.viewItem}>
                <div style={globalStyles.viewLabel}>Booked At</div>
                <div style={globalStyles.viewValue}>
                  {formatDate(view.createdAt)}
                </div>
              </div>
            </div>

            <div style={{ ...globalStyles.viewItem, marginTop: 14 }}>
              <div style={globalStyles.viewLabel}>Notes</div>
              <div style={globalStyles.viewValue}>
                {view.notes || view.remarks || "—"}
              </div>
            </div>

            <div
              style={{
                ...globalStyles.mrow,
                ...(isMobile ? { justifyContent: "stretch" } : {}),
              }}
            >
              <button
                type="button"
                style={{
                  ...globalStyles.cancel,
                  ...(isMobile ? { width: "100%" } : {}),
                }}
                onClick={() => setView(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
